import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  CardHeader,
  Collapse,
} from "reactstrap"
import Select from "react-select"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { triStateOptionsWithCounts, getTriStateValue } from "helpers/filterHelpers"

export default function StockFilters({
  params,
  updateParams,
  isAdvancedOpen,
  isFiltersOpen,
  setIsAdvancedOpen,
  data: filterData,
}) {
  const {
    isLoading,
    brands: brandsData,
    models: modelsData,
    packages: packagesData,
    engineCodes: engineCodesData,
    chassisCodes: chassisCodesData,
    engineCCs: engineCCsData,
    features: featuresData,
    years: yearsData,
    exteriorColors: exteriorColorsData,
    interiorColors: interiorColorsData,
    statuses: statusesData,
    auctionGrades: auctionGradesData,
    transmissions: transmissionsData,
    engineTypes: engineTypesData,
    showrooms: showroomsData,
    fuelTypes: fuelTypesData,
    keyTypes: keyTypesData,
    locations: locationsData,
    importers: importersData,
    booleanCounts,
  } = useFilteredVehicleOptions(params)
  

  const buildOptions = data =>
    data.map(item => ({
      value: item.slug,
      label: item.label,
    }))

  const handleMultiSelect = (key, selected) => {
    const values = selected ? selected.map(s => s.value) : []
    updateParams(prev => ({
      ...prev,
      [key]: values.join(","),
      page: 1,
    }))
  }

  const getSelectedValues = (key, data) => {
    const values = params[key]?.split(",") || []
    return buildOptions(data).filter(opt => values.includes(opt.value))
  }

  const handleNumericChange = (key, value) => {
    updateParams(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const handleSortChange = (key, value) => {
    updateParams(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const handleSelectChange = (key, value) => {
    updateParams(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const isPublishedOptions = triStateOptionsWithCounts("Published", "Draft", booleanCounts?.is_published?.published, booleanCounts?.is_published?.draft)
  const readyStockOptions = triStateOptionsWithCounts("Ready Stock", "Not Ready", booleanCounts?.vs_ready_stock?.ready, booleanCounts?.vs_ready_stock?.not_ready)
  const hasImagesOptions = triStateOptionsWithCounts("Has Images", "No Images", booleanCounts?.has_images?.with_images, booleanCounts?.has_images?.without_images)
  const hasPriceOptions = triStateOptionsWithCounts("Has Price", "No Price", booleanCounts?.has_price?.with_price, booleanCounts?.has_price?.without_price)
  const isSoldOptions = triStateOptionsWithCounts("Sold", "Not Sold", booleanCounts?.is_sold?.sold, booleanCounts?.is_sold?.not_sold)

  const sortOptions = [
    { value: "created_at", label: "Date Created" },
    { value: "updated_at", label: "Last Updated" },
    { value: "asking_price", label: "Price" },
    { value: "mileage", label: "Mileage" },
    { value: "year", label: "Year" },
  ]

  const sortOrderOptions = [
    { value: "desc", label: "Descending" },
    { value: "asc", label: "Ascending" },
  ]

  return (
    <div className="mb-3">
      {isFiltersOpen && (
        <>
          {/* 🔍 Common Filters */}
          <Row className="mb-3">
            <Col sm={12}>
              <small className="text-success">
                {isLoading ? "Option fetching..." : ""}
              </small>
            </Col>

            {/* Brand */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Brand</label>
              <Select
                isMulti
                value={getSelectedValues("brand", brandsData)}
                onChange={selected => handleMultiSelect("brand", selected)}
                options={buildOptions(brandsData)}
              />
            </Col>

            {/* Model */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Model</label>
              <Select
                isMulti
                value={getSelectedValues("model", modelsData)}
                onChange={selected => handleMultiSelect("model", selected)}
                options={buildOptions(modelsData)}
              />
            </Col>

            {/* Year */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Year</label>
              <Select
                isMulti
                value={getSelectedValues("year", yearsData)}
                onChange={selected => handleMultiSelect("year", selected)}
                options={buildOptions(yearsData)}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Engine Type</label>
              <Select
                isMulti
                value={getSelectedValues("engine_type", engineTypesData)}
                onChange={selected =>
                  handleMultiSelect("engine_type", selected)
                }
                options={buildOptions(engineTypesData)}
              />
            </Col>

            {/* Package */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Package</label>
              <Select
                isMulti
                value={getSelectedValues("package", packagesData)}
                onChange={selected => handleMultiSelect("package", selected)}
                options={buildOptions(packagesData)}
              />
            </Col>

            {/* Colors */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Color</label>
              <Select
                isMulti
                value={getSelectedValues("exterior_color", exteriorColorsData)}
                onChange={selected =>
                  handleMultiSelect("exterior_color", selected)
                }
                options={buildOptions(exteriorColorsData)}
              />
            </Col>

            {/* Status */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Status</label>
              <Select
                isMulti
                value={getSelectedValues("status", statusesData)}
                onChange={selected => handleMultiSelect("status", selected)}
                options={buildOptions(statusesData)}
              />
            </Col>

            {/* Location */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Location</label>
              <Select
                isMulti
                value={getSelectedValues("location", locationsData)}
                onChange={selected => handleMultiSelect("location", selected)}
                options={buildOptions(locationsData)}
              />
            </Col>

            {/* Showroom */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Showroom</label>
              <Select
                isMulti
                value={getSelectedValues("showroom", showroomsData)}
                onChange={selected => handleMultiSelect("showroom", selected)}
                options={buildOptions(showroomsData)}
              />
            </Col>

            {/* Auction & Technical */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Auction Grade</label>
              <Select
                isMulti
                value={getSelectedValues("auction_grade", auctionGradesData)}
                onChange={selected =>
                  handleMultiSelect("auction_grade", selected)
                }
                options={buildOptions(auctionGradesData)}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Engine CC</label>
              <Select
                isMulti
                value={getSelectedValues("engine_cc", engineCCsData)}
                onChange={selected => handleMultiSelect("engine_cc", selected)}
                options={buildOptions(engineCCsData)}
              />
            </Col>

            <CanView access={[ACCESS.STOCK.FILTER_IMPORTER]}>
              <Col sm={3} lg={3} xl={2}>
                <label className="control-label">Importer</label>
                <Select
                  isMulti
                  value={getSelectedValues("importer", importersData)}
                  onChange={selected => handleMultiSelect("importer", selected)}
                  options={buildOptions(importersData)}
                />
              </Col>
            </CanView>

            {/* Boolean / tri-state filters */}
            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Is Published</label>
              <Select
                value={getTriStateValue(isPublishedOptions, params.is_published)}
                onChange={selected =>
                  handleSelectChange("is_published", selected.value)
                }
                options={isPublishedOptions}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Images</label>
              <Select
                value={getTriStateValue(hasImagesOptions, params.has_images)}
                onChange={selected =>
                  handleSelectChange("has_images", selected.value)
                }
                options={hasImagesOptions}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Ready Stock</label>
              <Select
                value={getTriStateValue(readyStockOptions, params.vs_ready_stock)}
                onChange={selected =>
                  handleSelectChange("vs_ready_stock", selected.value)
                }
                options={readyStockOptions}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Price</label>
              <Select
                value={getTriStateValue(hasPriceOptions, params.has_price)}
                onChange={selected =>
                  handleSelectChange("has_price", selected.value)
                }
                options={hasPriceOptions}
              />
            </Col>

            <Col sm={3} lg={3} xl={2}>
              <label className="control-label">Sold</label>
              <Select
                value={getTriStateValue(isSoldOptions, params.is_sold)}
                onChange={selected =>
                  handleSelectChange("is_sold", selected.value)
                }
                options={isSoldOptions}
              />
            </Col>
          </Row>

          {/* 🔧 Advanced Filters */}
          <Card>
            <CardHeader
              style={{ cursor: "pointer" }}
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
              className="d-flex justify-content-between align-items-center"
            >
              <h6 className="mb-0">🔧 Advanced Filters</h6>
              <i
                className={`fas fa-chevron-${isAdvancedOpen ? "up" : "down"}`}
              />
            </CardHeader>
            <Collapse isOpen={isAdvancedOpen}>
              <CardBody>
                <Row>
                  {/* Transmission */}
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Transmission</label>
                    <Select
                      isMulti
                      value={getSelectedValues(
                        "transmission",
                        transmissionsData
                      )}
                      onChange={selected =>
                        handleMultiSelect("transmission", selected)
                      }
                      options={buildOptions(transmissionsData)}
                    />
                  </Col>
                  {/* Price Range */}
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Price Min</label>
                    <Input
                      type="number"
                      value={params.price_min || ""}
                      placeholder="Min"
                      onChange={e =>
                        handleNumericChange("price_min", e.target.value)
                      }
                    />
                  </Col>
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Price Max</label>
                    <Input
                      type="number"
                      value={params.price_max || ""}
                      placeholder="Max"
                      onChange={e =>
                        handleNumericChange("price_max", e.target.value)
                      }
                    />
                  </Col>

                  {/* Mileage Range */}
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Mileage Min</label>
                    <Input
                      type="number"
                      value={params.mileage_min || ""}
                      placeholder="Min"
                      onChange={e =>
                        handleNumericChange("mileage_min", e.target.value)
                      }
                    />
                  </Col>
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Mileage Max</label>
                    <Input
                      type="number"
                      value={params.mileage_max || ""}
                      placeholder="Max"
                      onChange={e =>
                        handleNumericChange("mileage_max", e.target.value)
                      }
                    />
                  </Col>
                  {/* Sorting & Results */}
                  <Col sm={2}>
                    <label className="control-label">Sort By</label>
                    <Select
                      value={sortOptions.find(
                        opt => opt.value === (params.sort_by || "created_at")
                      )}
                      onChange={selected =>
                        handleSortChange("sort_by", selected.value)
                      }
                      options={sortOptions}
                    />
                  </Col>

                  <Col sm={2}>
                    <label className="control-label">Sort Order</label>
                    <Select
                      value={sortOrderOptions.find(
                        opt => opt.value === (params.sort_order || "desc")
                      )}
                      onChange={selected =>
                        handleSortChange("sort_order", selected.value)
                      }
                      options={sortOrderOptions}
                    />
                  </Col>

                  <Col sm={2}>
                    <label className="control-label">Results Limit</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={params.limit || ""}
                      placeholder="Max 100"
                      onChange={e =>
                        handleNumericChange("limit", e.target.value)
                      }
                    />
                  </Col>

                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Interior Color</label>
                    <Select
                      isMulti
                      value={getSelectedValues(
                        "interior_color",
                        interiorColorsData
                      )}
                      onChange={selected =>
                        handleMultiSelect("interior_color", selected)
                      }
                      options={buildOptions(interiorColorsData)}
                    />
                  </Col>

                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Engine Code</label>
                    <Select
                      isMulti
                      value={getSelectedValues("engine_code", engineCodesData)}
                      onChange={selected =>
                        handleMultiSelect("engine_code", selected)
                      }
                      options={buildOptions(engineCodesData)}
                    />
                  </Col>

                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Chassis Code</label>
                    <Select
                      isMulti
                      value={getSelectedValues(
                        "chassis_code",
                        chassisCodesData
                      )}
                      onChange={selected =>
                        handleMultiSelect("chassis_code", selected)
                      }
                      options={buildOptions(chassisCodesData)}
                    />
                  </Col>

                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Fuel Type</label>
                    <Select
                      isMulti
                      value={getSelectedValues("fuel_type", fuelTypesData)}
                      onChange={selected =>
                        handleMultiSelect("fuel_type", selected)
                      }
                      options={buildOptions(fuelTypesData)}
                    />
                  </Col>

                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Key Type</label>
                    <Select
                      isMulti
                      value={getSelectedValues("key_type", keyTypesData)}
                      onChange={selected =>
                        handleMultiSelect("key_type", selected)
                      }
                      options={buildOptions(keyTypesData)}
                    />
                  </Col>

                  {/* Features */}
                  <Col sm={3} lg={3} xl={2}>
                    <label className="control-label">Features</label>
                    <Select
                      isMulti
                      value={getSelectedValues("feature", featuresData)}
                      onChange={selected =>
                        handleMultiSelect("feature", selected)
                      }
                      options={buildOptions(featuresData)}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* 🔄 Reset & Actions */}
          <Row className="mt-3">
            <Col sm={12}>
              <div className="d-flex gap-2 justify-content-between align-items-center">
                <div>
                  {isAdvancedOpen && (
                    <span className="text-muted">
                      Showing{" "}
                      {
                        Object.keys(params).filter(
                          key =>
                            params[key] && !["page", "per_page"].includes(key)
                        ).length
                      }{" "}
                      active filters
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  >
                    {isAdvancedOpen ? "▲ Hide" : "▼ Show"} Advanced Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      updateParams({
                        search: "",
                        brand: "",
                        model: "",
                        year: "",
                        package: "",
                        exterior_color: "",
                        interior_color: "",
                        price_min: "",
                        price_max: "",
                        mileage_min: "",
                        mileage_max: "",
                        showroom: "",
                        status: "",
                        location: "",
                        auction_grade: "",
                        transmission: "",
                        engine_type: "",
                        engine_code: "",
                        chassis_code: "",
                        engine_cc: "",
                        importer: "",
                        fuel_type: "",
                        key_type: "",
                        feature: "",
                        is_published: "",
                        vs_ready_stock: "",
                        has_images: "",
                        has_price: "",
                        is_sold: "",
                        sort_by: "created_at",
                        sort_order: "desc",
                        limit: "",
                        page: 1,
                      })
                    }}
                  >
                    Reset All Filters
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
